import { ApplicationState } from '@app/store'
import { errorToast, successToast } from '@app/utils/notifications'
import { makeRequest, RequestMethod } from '@app/utils/request'
import { Record } from 'immutable'
import { push } from 'connected-react-router'
import { Action, Reducer } from 'redux'
import { fork, put, select, takeLatest } from 'redux-saga/effects'
import { createSelector } from 'reselect'

// -------------------------
// STATE
// -------------------------

export interface IForgotPasswordState {
  readonly error?: string
  readonly hasRequestedPin: boolean
  readonly isSaving: boolean
  readonly isValid: boolean
  readonly password: string
  readonly pinCode: string
  readonly username: string
  readonly verifyPassword: string
}

const ForgotPasswordStateRecord = Record({
  error: undefined,
  hasRequestedPin: false,
  isSaving: false,
  isValid: false,
  password: '',
  pinCode: '',
  username: '',
  verifyPassword: '',
})

export class ForgotPasswordState extends ForgotPasswordStateRecord
  implements IForgotPasswordState {
  public static fromJS(initialState?: Partial<IForgotPasswordState>) {
    return new ForgotPasswordState(initialState)
  }

  public readonly error?: string
  public readonly hasRequestedPin: boolean
  public readonly isSaving: boolean
  public readonly isValid: boolean
  public readonly password: string
  public readonly pinCode: string
  public readonly username: string
  public readonly verifyPassword: string

  constructor(props?: Partial<ForgotPasswordState>) {
    props ? super(props) : super()
  }

  public validate(): ForgotPasswordState {
    let isValid = true

    if (!this.username) {
      isValid = false
    }

    if (this.hasRequestedPin) {
      if (!this.pinCode || !this.password || !this.verifyPassword) {
        isValid = false
      } else if (this.password !== this.verifyPassword) {
        isValid = false
      }
    }

    return this.set('isValid', isValid) as ForgotPasswordState
  }
}

// -------------------------
// ACTIONS
// -------------------------
export enum ActionTypes {
  ResetPassword = 'FORGOT_PASSWORD::RESET_PASSWORD',
  ResetPasswordFailure = 'FORGOT_PASSWORD::RESET_PASSWORD_FAILURE',
  ResetPasswordRequest = 'FORGOT_PASSWORD::RESET_PASSWORD_REQUEST',
  ResetPasswordSuccess = 'FORGOT_PASSWORD::RESET_PASSWORD_SUCCESS',
  ResetState = 'FORGOT_PASSWORD::RESET_STATE',
  SendPin = 'FORGOT_PASSWORD::SEND_PIN',
  SendPinFailure = 'FORGOT_PASSWORD::SEND_PIN_FAILURE',
  SendPinRequest = 'FORGOT_PASSWORD::SEND_PIN_REQUEST',
  SendPinSuccess = 'FORGOT_PASSWORD::SEND_PIN_SUCCESS',
  SetPassword = 'FORGOT_PASSWORD::SET_PASSWORD',
  SetPinCode = 'FORGOT_PASSWORD::SET_PIN_CODE',
  SetUsername = 'FORGOT_PASSWORD::SET_USERNAME',
  SetVerifyPassword = 'FORGOT_PASSWORD::SET_VERIFY_PASSWORD',
}

interface IResetPasswordAction {
  type: ActionTypes.ResetPassword
}

interface IResetPasswordFailureAction {
  error: string
  type: ActionTypes.ResetPasswordFailure
}

interface IResetPasswordRequestAction {
  type: ActionTypes.ResetPasswordRequest
}

interface IResetPasswordSuccessAction {
  type: ActionTypes.ResetPasswordSuccess
}

interface IResetStateAction {
  type: ActionTypes.ResetState
}

interface ISendPinAction {
  type: ActionTypes.SendPin
}

interface ISendPinFailureAction {
  error: string
  type: ActionTypes.SendPinFailure
}

interface ISendPinRequestAction {
  type: ActionTypes.SendPinRequest
}

interface ISendPinSuccessAction {
  type: ActionTypes.SendPinSuccess
}

interface ISetPasswordAction {
  password: string
  type: ActionTypes.SetPassword
}

interface ISetPinCodeAction {
  pinCode: string
  type: ActionTypes.SetPinCode
}

interface ISetUsernameAction {
  username: string
  type: ActionTypes.SetUsername
}

interface ISetVerifyPasswordAction {
  verifyPassword: string
  type: ActionTypes.SetVerifyPassword
}

// Declare a 'discriminated union' type. This guarantees that all references to 'type' properties contain one of the
// declared type strings (and not any other arbitrary string).
type KnownAction =
  | IResetPasswordFailureAction
  | IResetPasswordRequestAction
  | IResetPasswordSuccessAction
  | IResetStateAction
  | ISendPinFailureAction
  | ISendPinRequestAction
  | ISendPinSuccessAction
  | ISetPasswordAction
  | ISetPinCodeAction
  | ISetUsernameAction
  | ISetVerifyPasswordAction

// -------------------------
// ACTION CREATORS
// -------------------------
function resetPassword(): IResetPasswordAction {
  return { type: ActionTypes.ResetPassword }
}

function resetPasswordFailure(error: string): IResetPasswordFailureAction {
  return {
    error,
    type: ActionTypes.ResetPasswordFailure,
  }
}

function resetPasswordRequest(): IResetPasswordRequestAction {
  return { type: ActionTypes.ResetPasswordRequest }
}

function resetPasswordSuccess(): IResetPasswordSuccessAction {
  return { type: ActionTypes.ResetPasswordSuccess }
}

function resetState(): IResetStateAction {
  return { type: ActionTypes.ResetState }
}

function sendPin(): ISendPinAction {
  return { type: ActionTypes.SendPin }
}

function sendPinFailure(error: string): ISendPinFailureAction {
  return {
    error,
    type: ActionTypes.SendPinFailure,
  }
}

function sendPinRequest(): ISendPinRequestAction {
  return { type: ActionTypes.SendPinRequest }
}

function sendPinSuccess(): ISendPinSuccessAction {
  return { type: ActionTypes.SendPinSuccess }
}

function setPassword(password: string): ISetPasswordAction {
  return {
    password,
    type: ActionTypes.SetPassword,
  }
}

function setPinCode(pinCode: string): ISetPinCodeAction {
  return {
    pinCode,
    type: ActionTypes.SetPinCode,
  }
}

function setUsername(username: string): ISetUsernameAction {
  return {
    type: ActionTypes.SetUsername,
    username,
  }
}

function setVerifyPassword(verifyPassword: string): ISetVerifyPasswordAction {
  return {
    type: ActionTypes.SetVerifyPassword,
    verifyPassword,
  }
}

export const actionCreators = {
  resetPassword,
  resetState,
  sendPin,
  setPassword,
  setPinCode,
  setUsername,
  setVerifyPassword,
}

// -------------------------
// REDUCER
// -------------------------

export const reducer: Reducer<ForgotPasswordState> = (
  state: ForgotPasswordState = new ForgotPasswordState(),
  incomingAction: Action,
) => {
  const action = incomingAction as KnownAction
  switch (action.type) {
    case ActionTypes.ResetPasswordFailure:
      return state
        .set('error', action.error)
        .set('isSaving', false) as ForgotPasswordState

    case ActionTypes.ResetPasswordRequest:
      return state.set('isSaving', true) as ForgotPasswordState

    case ActionTypes.ResetPasswordSuccess:
      return state.set('isSaving', false) as ForgotPasswordState

    case ActionTypes.ResetState:
      return new ForgotPasswordState()

    case ActionTypes.SendPinFailure:
      return state
        .set('error', action.error)
        .set('isSaving', false) as ForgotPasswordState

    case ActionTypes.SendPinRequest:
      return state.set('isSaving', true) as ForgotPasswordState

    case ActionTypes.SendPinSuccess:
      return state
        .set('error', undefined)
        .set('hasRequestedPin', true)
        .set('isSaving', false)
        .set('isValid', false) as ForgotPasswordState

    case ActionTypes.SetPassword: {
      const newState = state.set(
        'password',
        action.password,
      ) as ForgotPasswordState
      return newState.validate()
    }

    case ActionTypes.SetPinCode: {
      const newState = state.set(
        'pinCode',
        action.pinCode,
      ) as ForgotPasswordState
      return newState.validate()
    }

    case ActionTypes.SetUsername: {
      const newState = state.set(
        'username',
        action.username,
      ) as ForgotPasswordState
      return newState.validate()
    }

    case ActionTypes.SetVerifyPassword: {
      const newState = state.set(
        'verifyPassword',
        action.verifyPassword,
      ) as ForgotPasswordState
      return newState.validate()
    }

    default:
      // The following line guarantees that every action in the KnownAction union has been covered by a case above
      const exhaustiveCheck: never = action
  }

  return state
}

// -------------------------
// SAGAS
// -------------------------
function* resetPasswordSaga() {
  try {
    yield put(resetPasswordRequest())

    const state: ForgotPasswordState = yield select(selectModuleState())

    const reqOpts = {
      body: JSON.stringify({
        password: state.password,
        pin: state.pinCode,
        username: state.username,
      }),
      method: RequestMethod.POST,
    }

    const result = yield makeRequest('/api/Users/ResetPassword', reqOpts)

    if (result.success === true) {
      yield put(resetPasswordSuccess())
      yield put(push('/login'))
    } else {
      yield put(resetPasswordFailure(result.message))
      yield put(errorToast('Oops!', result.message))
    }
  } catch (err) {
    yield put(resetPasswordFailure(err.message))
    yield put(errorToast('Oops!', err.message))
  }
}

function* sendPinSaga() {
  try {
    yield put(sendPinRequest())

    const state: ForgotPasswordState = yield select(selectModuleState())

    const reqOpts = {
      body: JSON.stringify({
        username: state.username,
      }),
      method: RequestMethod.POST,
    }

    const result = yield makeRequest('/api/Users/ForgotPassword', reqOpts)

    if (result.success === true) {
      yield put(sendPinSuccess())
      yield put(
        successToast(
          'Success!',
          'Check your email for your password reset pin code.',
        ),
      )
    } else {
      yield put(sendPinFailure(result.message))
      yield put(errorToast('Oops!', result.message))
    }
  } catch (err) {
    yield put(sendPinFailure(err.message))
    yield put(errorToast('Oops!', err.message))
  }
}

function* watchResetPassword() {
  yield takeLatest(ActionTypes.ResetPassword, resetPasswordSaga)
}

function* watchSendPin() {
  yield takeLatest(ActionTypes.SendPin, sendPinSaga)
}

export function* forgotPasswordRootSaga() {
  yield fork(watchResetPassword)
  yield fork(watchSendPin)
}

// -------------------------
// SELECTORS
// -------------------------
const selectModuleState = () => (
  state: ApplicationState,
): ForgotPasswordState => state.forgotPassword

export const selectError = () =>
  createSelector(selectModuleState(), (mState) => mState.error)

export const selectHasRequestedPin = () =>
  createSelector(selectModuleState(), (mState) => mState.hasRequestedPin)

export const selectIsSaving = () =>
  createSelector(selectModuleState(), (mState) => mState.isSaving)

export const selectIsValid = () =>
  createSelector(selectModuleState(), (mState) => mState.isValid)

export const selectPassword = () =>
  createSelector(selectModuleState(), (mState) => mState.password)

export const selectPinCode = () =>
  createSelector(selectModuleState(), (mState) => mState.pinCode)

export const selectUsername = () =>
  createSelector(selectModuleState(), (mState) => mState.username)

export const selectVerifyPassword = () =>
  createSelector(selectModuleState(), (mState) => mState.verifyPassword)
